import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ActionTabs, AppIcon } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { selectProfilePhoto } from '@backrs_ui/grow-ui/src/redux/user/user';
import type { UserCollege } from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.types';

import { Avatar, Box, Button, SxProps, Theme, Typography } from '@mui/material';

import { AddCollegeSearch } from './AddCollegeSearch';
import { useToolActions } from '../useToolActions';

const STRINGS = appStrings.modules.myColleges;

type MyCollegesHeaderProps = Readonly<{ colleges: readonly UserCollege[] }>;

export const MyCollegesHeader = ({ colleges }: MyCollegesHeaderProps) => {
  const navigate = useNavigate();
  const profilePhoto = useAppSelector(selectProfilePhoto);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const actions = useToolActions('my_college_list', APP_ROUTE.MY_COLLEGES, 'my-colleges');

  return (
    <Box id="my-colleges-header" sx={cardSx}>
      <Box sx={topRowSx}>
        <Box sx={titleSx}>
          <Avatar src={profilePhoto ?? undefined} alt="" sx={avatarSx} />
          <Box>
            <Typography variant="text-xl-semibold" component="h1">
              {STRINGS.header.title}
            </Typography>
            <Typography variant="text-md-regular" sx={subtitleSx}>
              {STRINGS.header.subtitle}
            </Typography>
          </Box>
        </Box>
        <Box sx={actionsSx}>
          <Button
            id="my-colleges-compare-button"
            hierarchy="Secondary color"
            size="md"
            icon="Leading"
            startIcon={<AppIcon iconName="Scales01" diameter="16px" color="currentColor" />}
            onClick={() => navigate(APP_ROUTE.COMPARE_COLLEGES)}
          >
            {STRINGS.compareButton}
          </Button>
          {actions.length > 0 && (
            <ActionTabs
              actions={actions}
              showCopySuccess={showCopySuccess}
              onCopySuccessChange={setShowCopySuccess}
            />
          )}
        </Box>
      </Box>
      <AddCollegeSearch colleges={colleges} />
    </Box>
  );
};

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  padding: backrs.spacing['spacing-xl'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const topRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-lg'],
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  minWidth: 0,
});

const avatarSx: SxProps<Theme> = ({ backrs }) => ({
  width: backrs.spacing['spacing-6xl'],
  height: backrs.spacing['spacing-6xl'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  flexShrink: 0,
});

const subtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const actionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-md'],
  '& #my-colleges-compare-button': { whiteSpace: 'nowrap' },
  '& .MuiTabs-root': { minHeight: 'auto' },
});
